import { create } from 'zustand';

export interface PinnedRun {
  runId: string;
  srKey: string;
  texecKey: string;
  gameName: string;
  studio?: string;
  pinnedAt: string;
}

interface SessionRunsState {
  email: string | null;
  pinned: PinnedRun[];
  /** Bind storage key to the signed-in user; reloads from localStorage. */
  bindUser: (email: string | null) => void;
  pin: (run: Omit<PinnedRun, 'pinnedAt'> & { pinnedAt?: string }) => void;
  unpin: (runId: string) => void;
  clear: () => void;
}

function storageKey(email: string): string {
  return `sidekick:open-runs:${email.trim().toLowerCase()}`;
}

function readPinned(email: string): PinnedRun[] {
  try {
    const raw = localStorage.getItem(storageKey(email));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PinnedRun[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p) => p && typeof p.runId === 'string' && p.runId);
  } catch {
    return [];
  }
}

function writePinned(email: string, pinned: PinnedRun[]) {
  try {
    localStorage.setItem(storageKey(email), JSON.stringify(pinned));
  } catch {
    // quota / private mode — ignore
  }
}

/**
 * Personal watchlist of runs opened this browser session (and across refresh).
 * Dismiss only via X on the Active strip; server jobs keep running.
 */
export const useSessionRunsStore = create<SessionRunsState>((set, get) => ({
  email: null,
  pinned: [],

  bindUser(email) {
    if (!email) {
      set({ email: null, pinned: [] });
      return;
    }
    const key = email.trim().toLowerCase();
    if (get().email === key) return;
    set({ email: key, pinned: readPinned(key) });
  },

  pin(run) {
    const email = get().email;
    if (!email || !run.runId) return;
    const existing = get().pinned;
    if (existing.some((p) => p.runId === run.runId)) {
      // Refresh metadata if we already have it.
      const next = existing.map((p) =>
        p.runId === run.runId
          ? {
              ...p,
              srKey: run.srKey || p.srKey,
              texecKey: run.texecKey || p.texecKey,
              gameName: run.gameName || p.gameName,
              studio: run.studio || p.studio,
            }
          : p,
      );
      writePinned(email, next);
      set({ pinned: next });
      return;
    }
    const entry: PinnedRun = {
      runId: run.runId,
      srKey: run.srKey,
      texecKey: run.texecKey,
      gameName: run.gameName,
      studio: run.studio,
      pinnedAt: run.pinnedAt ?? new Date().toISOString(),
    };
    const next = [entry, ...existing];
    writePinned(email, next);
    set({ pinned: next });
  },

  unpin(runId) {
    const email = get().email;
    if (!email) return;
    const next = get().pinned.filter((p) => p.runId !== runId);
    writePinned(email, next);
    set({ pinned: next });
  },

  clear() {
    const email = get().email;
    if (email) {
      try {
        localStorage.removeItem(storageKey(email));
      } catch {
        /* ignore */
      }
    }
    set({ pinned: [] });
  },
}));
